import type { Edge, Node } from '@xyflow/react';
import {
  ANY_T,
  exprToCoarse,
  schemaToTypeExpr,
  type TypeExpr,
  unifyTypeExpr,
  valueToTypeExpr,
  typeExprEquals,
} from '@features/flows/utils/types';
import type { NodeTemplate, TypeLink, FromRef, LinkOp, LinkOpObject } from '@features/flows/palette/registry';
import { arrayOf, recordOf } from '@features/flows/utils/types';

// ---------- Path-aware projection (shapePath) helpers -----------------------
type ShapePathOp = {
  name: 'shapePath';
  pathAttr?: string;
  path?: string;
  delimiter?: string; // default "."
  maxDepth?: number;  // default 32
  arrayPolicy?: 'map'; // reserved for future: 'index'
};

function splitPathToSegments(raw: string, delimiter = '.'): string[] {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return [];
  return trimmed
    .split(delimiter)
    .map((s) => s.trim())
    .filter(Boolean);
}

function projectPathType(
  t: TypeExpr,
  segs: string[],
  opts?: { depth?: number; maxDepth?: number }
): TypeExpr {
  const depth = opts?.depth ?? 0;
  const cap = Math.max(1, opts?.maxDepth ?? 32);
  if (depth > cap) return ANY_T;
  if (segs.length === 0) return t;
  const [head, ...tail] = segs;
  switch (t.tag) {
    case 'shape': {
      const props = (t as any).props as Record<string, TypeExpr>;
      const next: TypeExpr | undefined = props?.[head] ?? (t as any).additional;
      return next ? projectPathType(next, tail, { depth: depth + 1, maxDepth: cap }) : ANY_T;
    }
    case 'record': {
      const value = (t as any).value as TypeExpr | undefined;
      return projectPathType(value ?? ANY_T, tail, { depth: depth + 1, maxDepth: cap });
    }
    case 'array': {
      const elem = (t as any).elem as TypeExpr | undefined;
      const projectedElem = projectPathType(elem ?? ANY_T, tail, { depth: depth + 1, maxDepth: cap });
      return arrayOf(projectedElem);
    }
    case 'tuple': {
      const elems = (t as any).elems as TypeExpr[] | undefined;
      let acc: TypeExpr = ANY_T;
      if (Array.isArray(elems) && elems.length) {
        for (const e of elems) acc = unifyTypeExpr(acc, projectPathType(e, tail, { depth: depth + 1, maxDepth: cap }));
      }
      const rest = (t as any).rest as TypeExpr | undefined;
      if (rest) acc = unifyTypeExpr(acc, projectPathType(rest, tail, { depth: depth + 1, maxDepth: cap }));
      return acc;
    }
    case 'any':
    case 'var':
    case 'coarse':
    default:
      return ANY_T;
  }
}


type PinTypeMap = { in: Record<string, TypeExpr>; out: Record<string, TypeExpr> };
export type InferredMap = Record<string, PinTypeMap>;

const getTpl = (templates: Map<string, NodeTemplate> | null | undefined, n: Node) => {
  const key = (n.data as any)?.templateKey || (n.data as any)?.handler || '';
  return key && templates ? templates.get(String(key)) : undefined;
};

function initialPinTypes(nodes: Node[], templates: Map<string, NodeTemplate> | null | undefined): InferredMap {
  const out: InferredMap = {};
  for (const n of nodes) {
    const tpl = getTpl(templates, n);
    const inMap: Record<string, TypeExpr> = {};
    const outMap: Record<string, TypeExpr> = {};
    const pins = (n.data as any)?.pins;
    const inPins = pins?.data?.in ?? [];
    const outPins = pins?.data?.out ?? [];
    for (const p of inPins) {
      const te = (p as any).typeExpr as TypeExpr | undefined
        ?? (tpl?.ports?.data?.in?.find((x) => x.id === p.id)?.typeExpr)
        ?? schemaToTypeExpr(undefined);
      inMap[p.id] = te ?? ANY_T;
    }
    for (const p of outPins) {
      const te = (p as any).typeExpr as TypeExpr | undefined
        ?? (tpl?.ports?.data?.out?.find((x) => x.id === p.id)?.typeExpr)
        ?? schemaToTypeExpr(undefined);
      outMap[p.id] = te ?? ANY_T;
    }
    out[n.id] = { in: inMap, out: outMap };
  }
  return out;
}

function isAny(t: TypeExpr | undefined): boolean {
  return !t || t.tag === 'any';
}

function normalizeOps(op?: LinkOp | LinkOp[]): (LinkOp | LinkOpObject)[] {
  if (!op) return [];
  if (Array.isArray(op)) return op as any;
  return [op as any];
}

function applyOpPipeline(seed: TypeExpr, ops: (LinkOp | LinkOpObject)[], node: Node): TypeExpr {
  let t: TypeExpr = seed;
  for (const op of ops) {
    if (typeof op === 'string') {
      switch (op) {
        case 'element': t = t?.tag === 'array' ? (t.elem ?? ANY_T) : ANY_T; break;
        case 'value': t = (t as any)?.tag === 'record' ? ((t as any).value ?? ANY_T) : ANY_T; break;
        case 'arrayOf': t = arrayOf(t ?? ANY_T); break;
        case 'recordOf': t = recordOf({ tag: 'coarse', name: 'string' }, t ?? ANY_T); break;
        case 'entriesOf': t = { tag: 'tuple', elems: [{ tag: 'coarse', name: 'string' }, t ?? ANY_T] } as any; break;
        case 'schemaType': {
          // Only meaningful when the source is an attr; for pin-based, we lack runtime schema value
          // To support attr-driven pipelines, this op should be applied at source extraction time.
          // Here, keep as-is.
          break;
        }
        default: break;
      }
    } else if (typeof op === 'object' && op && (op as LinkOpObject).name) {
      const name = (op as any).name as string;
      if (name === 'flatten') {
        const depth = Math.max(1, Number((op as any).depth ?? 1));
        if (t.tag !== 'array') {
          t = ANY_T; // not flattenable
        } else {
          let elem = t.elem;
          let d = depth - 1;
          while (d > 0 && elem?.tag === 'array') { elem = elem.elem; d--; }
          // If not enough nesting, keep current array shape
          t = { tag: 'array', elem: elem ?? ANY_T };
        }
      } else if (name === 'tupleAt') {
        const idxAttr = (op as any).indexAttr as string | undefined;
        const rawIdx = idxAttr ? (node?.data as any)?.attrs?.[idxAttr] : (op as any).index;
        let idx = Number(rawIdx);
        if (!Number.isFinite(idx)) idx = 0;
        if (t.tag === 'tuple') {
          const n = t.elems.length;
          const i = idx < 0 ? Math.max(0, n + idx) : idx;
          t = (t.elems[i] ?? (t.rest ?? ANY_T));
        } else if (t.tag === 'array' && (t.elem as any)?.tag === 'tuple') {
          const tup = t.elem as any;
          const n = (tup.elems as any[])?.length ?? 0;
          const i = idx < 0 ? Math.max(0, n + idx) : idx;
          t = (tup.elems[i] ?? (tup.rest ?? ANY_T));
        } else {
          t = ANY_T;
        }
      } else if (name === 'shapeProp') {
        // Path-aware property projection from a shape using a node attr as the key
        const keyAttr = (op as any).keyAttr || 'key';
        const rawKey = (node?.data as any)?.attrs?.[keyAttr];
        const key = typeof rawKey === 'string' ? rawKey.trim() : '';
        if (!key) {
          t = ANY_T;
        } else if (t.tag === 'shape') {
          const segs = splitPathToSegments(key, '.');
          t = segs.length ? projectPathType(t, segs, { maxDepth: 32 }) : ANY_T;
        } else {
          t = ANY_T;
        }
      } else if (name === 'shapePath') {
        const p = op as ShapePathOp;
        const delimiter = typeof p.delimiter === 'string' && p.delimiter.length ? p.delimiter : '.';
        const rawFromAttr = p.pathAttr ? (node?.data as any)?.attrs?.[p.pathAttr] : undefined;
        const raw = typeof rawFromAttr === 'string' && rawFromAttr.trim().length
          ? rawFromAttr
          : (typeof p.path === 'string' ? p.path : '');
        const segs = splitPathToSegments(raw, delimiter);
        if (!segs.length) {
          t = ANY_T;
        } else {
          t = projectPathType(t ?? ANY_T, segs, { maxDepth: Math.max(1, Number(p.maxDepth ?? 32)) });
        }
      }
    }
  }
  return t;
}

function evalFromRef(pm: PinTypeMap, node: Node, ref: FromRef): TypeExpr {
  // attr-based: schemaType only (others default to ANY)
  if ('attr' in ref) {
    const ops = normalizeOps(ref.op);
    // Support schemaType => parse attrs[ref.attr] as JSON Schema
    const hasSchemaType = ops.some((o) => (typeof o === 'string' ? o === 'schemaType' : false));
    if (hasSchemaType) {
      const schema = (node.data as any)?.attrs?.[ref.attr];
      const parsed = schemaToTypeExpr(schema);
      // remove schemaType from pipeline and apply rest
      const rest = ops.filter((o) => !(typeof o === 'string' && o === 'schemaType')) as any;
      return applyOpPipeline(parsed, rest, node);
    }
    // literalType => map attrs[ref.attr] (JS literal) to TypeExpr when asConst is true
    const hasLiteralType = ops.some((o) => (typeof o === 'string' ? o === 'literalType' : (o as any)?.name === 'literalType'));
    if (hasLiteralType) {
      const asConst = !!((node?.data as any)?.attrs?.asConst);
      if (!asConst) return ANY_T;
      const val = (node.data as any)?.attrs?.[ref.attr];
      const parsed = valueToTypeExpr(val, { depth: 5, maxProps: 100, maxTuple: 64 });
      const rest = ops.filter((o) => !(typeof o === 'string' ? o === 'literalType' : (o as any)?.name === 'literalType')) as any;
      return applyOpPipeline(parsed, rest, node);
    }
    return ANY_T;
  }
  const base = ref.io === 'in' ? (pm.in[ref.id] ?? ANY_T) : (pm.out[ref.id] ?? ANY_T);
  const ops = normalizeOps(ref.op);
  return applyOpPipeline(base, ops, node);
}

function applyTypeLinks(inferred: InferredMap, nodes: Node[], templates: Map<string, NodeTemplate> | null | undefined) {
  for (const n of nodes) {
    const nid = n.id;
    const tpl = getTpl(templates, n);
    if (!tpl?.typeLinks?.length) continue;
    const pm = inferred[nid];
    if (!pm) continue;
    for (const link of tpl.typeLinks as TypeLink[]) {
      if ('from' in link) {
        const fromRef = link.from as FromRef;
        const ops = normalizeOps((fromRef as any).op);
        const isLiteralAttr = (
          'attr' in fromRef &&
          ops.some((o) => (typeof o === 'string' ? o === 'literalType' : (o as any)?.name === 'literalType'))
        );
        const projected = evalFromRef(pm, n, fromRef);
        const toKey = link.to.id;
        if (isLiteralAttr) {
          // For literalType from attrs (gated by asConst), prefer replacement semantics.
          // This ensures editing the literal updates the inferred type instead of widening via unify.
          if (link.to.io === 'in') {
            if (!isAny(projected)) pm.in[toKey] = projected; // keep prior if projected is ANY
          } else {
            if (!isAny(projected)) pm.out[toKey] = projected;
          }
        } else {
          const curr = link.to.io === 'in' ? pm.in[toKey] : pm.out[toKey];
          const next = unifyTypeExpr(curr ?? ANY_T, projected ?? ANY_T);
          if (link.to.io === 'in') pm.in[toKey] = next; else pm.out[toKey] = next;
        }
      } else if ('multiFrom' in link) {
        // support prefer: choose first non-any among sources (after per-source ops)
        const op = (link as any).op;
        if (op === 'prefer') {
          let chosen: TypeExpr = ANY_T;
          for (const src of link.multiFrom as FromRef[]) {
            const t = evalFromRef(pm, n, src);
            if (!isAny(t)) { chosen = t; break; }
          }
          const toKey = link.to.id;
          if (!isAny(chosen)) {
            // Replacement semantics for prefer: keep the first precise projection
            if (link.to.io === 'in') pm.in[toKey] = chosen; else pm.out[toKey] = chosen;
          } else {
            const curr = link.to.io === 'in' ? pm.in[toKey] : pm.out[toKey];
            const next = unifyTypeExpr(curr ?? ANY_T, chosen ?? ANY_T);
            if (link.to.io === 'in') pm.in[toKey] = next; else pm.out[toKey] = next;
          }
        }
      }
    }
  }
}

function unifyAcrossEdge(inferred: InferredMap, e: Edge) {
  const data = (e as any).data ?? {};
  if ((data.channel ?? 'exec') !== 'data') return;

  const src = inferred[e.source];
  const tgt = inferred[e.target];
  if (!src || !tgt) return;
  const srcRaw = data.rawSourceHandle ?? String(e.sourceHandle).split(':').pop()!;
  const tgtRaw = data.rawTargetHandle ?? String(e.targetHandle).split(':').pop()!;
  const ts = src.out[srcRaw] ?? ANY_T;
  const tt = tgt.in[tgtRaw] ?? ANY_T;

  // Check for live type mismatch (recomputed each inference pass)
  const srcCoarse = exprToCoarse(ts);
  const tgtCoarse = exprToCoarse(tt);
  const hasLiveMismatch = srcCoarse && tgtCoarse &&
                          srcCoarse !== 'any' && tgtCoarse !== 'any' &&
                          srcCoarse !== tgtCoarse;

  // Skip unification when types mismatch to preserve distinct types for autocast.
  // Exception: when types match but a mapping exists (e.g., object→object with field renames),
  // we still need to unify to propagate types through the edge.
  if (hasLiveMismatch) return;

  const unified = unifyTypeExpr(ts, tt);
  src.out[srcRaw] = unified;
  tgt.in[tgtRaw] = unified;
}

export function inferTypes(nodes: Node[], edges: Edge[], templates: Map<string, NodeTemplate> | null | undefined): InferredMap {
  const inferred = initialPinTypes(nodes, templates);
  const PASS_LIMIT = 6;
  for (let pass = 0; pass < PASS_LIMIT; pass++) {
    applyTypeLinks(inferred, nodes, templates);
    for (const e of edges) unifyAcrossEdge(inferred, e);
  }
  return inferred;
}

export function applyInferredTypesToNodes(nodes: Node[], inferred: InferredMap): Node[] {
  return applyInferredTypesToNodesWithChange(nodes, inferred).nodes;
}

export function applyInferredTypesToNodesWithChange(
  nodes: Node[],
  inferred: InferredMap,
): { nodes: Node[]; changed: boolean } {
  let changed = false;
  const next = nodes.map((n) => {
    const pm = inferred[n.id];
    if (!pm) return n;
    const pins = (n.data as any)?.pins;
    if (!pins) return n;
    const prevIn = (pins.data?.in ?? []) as any[];
    const prevOut = (pins.data?.out ?? []) as any[];

    let nextIn = prevIn;
    let nextOut = prevOut;
    let nodeChanged = false;

    // Avoid cloning when inference results are identical so ReactFlow nodes keep stable refs.
    for (let i = 0; i < prevIn.length; i++) {
      const pin = prevIn[i] as any;
      const prevTe = pin.typeExpr as TypeExpr | undefined;
      const te = pm.in[pin.id] ?? prevTe ?? ANY_T;
      const nextCoarse = exprToCoarse(te);
      const typeExprChanged = !prevTe || !typeExprEquals(prevTe, te);
      const coarseChanged = (pin.type ?? 'any') !== nextCoarse;
      if (typeExprChanged || coarseChanged) {
        if (nextIn === prevIn) nextIn = prevIn.slice();
        nextIn[i] = { ...pin, typeExpr: te, type: nextCoarse };
        nodeChanged = true;
      }
    }

    for (let i = 0; i < prevOut.length; i++) {
      const pin = prevOut[i] as any;
      const prevTe = pin.typeExpr as TypeExpr | undefined;
      const te = pm.out[pin.id] ?? prevTe ?? ANY_T;
      const nextCoarse = exprToCoarse(te);
      const typeExprChanged = !prevTe || !typeExprEquals(prevTe, te);
      const coarseChanged = (pin.type ?? 'any') !== nextCoarse;
      if (typeExprChanged || coarseChanged) {
        if (nextOut === prevOut) nextOut = prevOut.slice();
        nextOut[i] = { ...pin, typeExpr: te, type: nextCoarse };
        nodeChanged = true;
      }
    }

    if (!nodeChanged) return n;

    changed = true;

    const prevData = pins.data ?? {};
    let nextData = prevData;
    if (nextIn !== prevIn) {
      nextData = { ...nextData, in: nextIn };
    }
    if (nextOut !== prevOut) {
      nextData = { ...nextData, out: nextOut };
    }

    const nextPins = nextData === prevData ? pins : { ...pins, data: nextData };
    const nextNodeData = nextPins === pins ? n.data : { ...(n.data as any), pins: nextPins };

    if (nextNodeData === n.data) return n;
    return { ...n, data: nextNodeData } as Node;
  });
  return { nodes: next, changed };
}
